import type { InsInsightOutcomeResponse } from '@plato/data/prisma';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleHeading,
    CollapsiblePanel,
    CollapsibleSubtitle,
    CollapsibleTitle,
    CollapsibleTrigger,
} from '@plato/ui/containers/collapsible';
import { Button } from '@plato/ui/primitives/button';
import { Typography } from '@plato/ui/primitives/typography';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useInsightsWithAnalytics } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { isRiskInsightType } from '../../insights-common/lib/insight-bucket.ts';
import { useLogInteractionDialog } from '../log-interaction-dialog-provider.tsx';
import { LogInteractionInsightCard } from './log-interaction-insight-card.tsx';

const maxVisiblePerGroup = 3;

export function LogInteractionInsightList(): ReactNode {
    const { t } = useTranslation('notes');
    const {
        session,
        customerId,
        stagedInsInsightOutcomes,
        upsertInsInsightOutcome,
        removeInsInsightOutcome,
    } = useLogInteractionDialog();
    const sendEvent = useAnalytics();

    const { insights, isLoading } = useInsightsWithAnalytics({ customerId });

    const [showAllRisks, setShowAllRisks] = useState(false);
    const [showAllOpportunities, setShowAllOpportunities] = useState(false);

    const isEmpty = insights.length === 0;

    const risks = insights.filter((i) => isRiskInsightType(i.insight.insight_type));
    const opportunities = insights.filter((i) => !isRiskInsightType(i.insight.insight_type));

    const summaryParts = [
        { count: risks.length, label: t('risks', { ns: 'insights' }) },
        { count: opportunities.length, label: t('opportunities', { ns: 'insights' }) },
    ]
        .filter((part) => part.count > 0)
        .map((part) => `${part.count} ${part.label}`);

    const handleSelectResponse = (insightId: string, response: InsInsightOutcomeResponse): void => {
        // INTERESTED, NO_NEED and BUYS_ELSEWHERE are all RESOLVED responses.
        upsertInsInsightOutcome({
            insightId,
            response,
            status: 'RESOLVED',
        });

        sendEvent('log_insight_outcome_selected', {
            ...session,
            customer_id: customerId,
            sales_insight_id: insightId,
            response,
        });
    };

    const handleRemoveOutcome = (insightId: string): void => {
        removeInsInsightOutcome(insightId);

        sendEvent('log_insight_outcome_cleared', {
            ...session,
            customer_id: customerId,
            sales_insight_id: insightId,
        });
    };

    return (
        <Collapsible
            onOpenChange={(expanded) => {
                sendEvent('log_insight_section_toggled', {
                    ...session,
                    customer_id: customerId,
                    expanded,
                    // Undefined, not 0, while insights are loading: the section can open
                    // before its lists land, and a false empty would misread the denominator.
                    risk_count: isLoading ? undefined : risks.length,
                    opportunity_count: isLoading ? undefined : opportunities.length,
                });
            }}
        >
            <CollapsibleHeader>
                <CollapsibleHeading>
                    <CollapsibleTitle>{t('notes__ai_insights_section_title')}</CollapsibleTitle>
                    {summaryParts.length > 0 && (
                        <CollapsibleSubtitle>{summaryParts.join(' · ')}</CollapsibleSubtitle>
                    )}
                </CollapsibleHeading>
                <CollapsibleTrigger />
            </CollapsibleHeader>
            <CollapsiblePanel>
                {isEmpty ? (
                    <p className="text-body text-content-weak">
                        {isLoading
                            ? t('notes__ai_insights_section_loading')
                            : t('notes__ai_insights_section_empty')}
                    </p>
                ) : (
                    <div className="flex flex-col gap-default">
                        {risks.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('risks', { ns: 'insights' })}
                                </Typography>
                                {(showAllRisks ? risks : risks.slice(0, maxVisiblePerGroup)).map(
                                    (insightData) => (
                                        <LogInteractionInsightCard
                                            key={insightData.insight.id}
                                            insightData={{
                                                ...insightData,
                                                outcome: stagedInsInsightOutcomes.find(
                                                    (o) => o.insightId === insightData.insight.id,
                                                ),
                                            }}
                                            onExpand={() => {
                                                sendEvent('log_insight_card_expanded', {
                                                    ...session,
                                                    customer_id: customerId,
                                                    sales_insight_id: insightData.insight.id,
                                                });
                                            }}
                                            onSelectResponse={(response) => {
                                                handleSelectResponse(insightData.insight.id, response);
                                            }}
                                            onRemoveOutcome={() => {
                                                handleRemoveOutcome(insightData.insight.id);
                                            }}
                                        />
                                    ),
                                )}
                                {!showAllRisks && risks.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllRisks(true);
                                            sendEvent('log_insight_section_expanded', {
                                                ...session,
                                                customer_id: customerId,
                                                section: 'risks',
                                                total_count: risks.length,
                                            });
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: risks.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                        {opportunities.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('opportunities', { ns: 'insights' })}
                                </Typography>
                                {(showAllOpportunities
                                    ? opportunities
                                    : opportunities.slice(0, maxVisiblePerGroup)
                                ).map((insightData) => (
                                    <LogInteractionInsightCard
                                        key={insightData.insight.id}
                                        insightData={{
                                            ...insightData,
                                            outcome: stagedInsInsightOutcomes.find(
                                                (o) => o.insightId === insightData.insight.id,
                                            ),
                                        }}
                                        onExpand={() => {
                                            sendEvent('log_insight_card_expanded', {
                                                ...session,
                                                customer_id: customerId,
                                                sales_insight_id: insightData.insight.id,
                                            });
                                        }}
                                        onSelectResponse={(response) => {
                                            handleSelectResponse(insightData.insight.id, response);
                                        }}
                                        onRemoveOutcome={() => {
                                            handleRemoveOutcome(insightData.insight.id);
                                        }}
                                    />
                                ))}
                                {!showAllOpportunities && opportunities.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllOpportunities(true);
                                            sendEvent('log_insight_section_expanded', {
                                                ...session,
                                                customer_id: customerId,
                                                section: 'opportunities',
                                                total_count: opportunities.length,
                                            });
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: opportunities.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </CollapsiblePanel>
        </Collapsible>
    );
}
